/**
 * To-Do Screen - Fast "thought dump" experience for capturing and managing tasks.
 * Supports quick add, drag reorder, toggle completion, and scheduling as calendar events.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dimensions, Keyboard, type LayoutChangeEvent, Platform, Pressable, RefreshControl, StyleSheet, View } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Animated, { useAnimatedRef } from 'react-native-reanimated';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { CalendarEventModal } from '@/components/calendar/event-modal';
import { ScreenHeaderContainer } from '@/components/ui/ScreenHeaderContainer';
import { TaskListSkeleton } from '@/components/ui/TasksScreenSkeleton';
import { TodoQuickAddBar, type TodoQuickAddBarHandle } from '@/components/todo/TodoQuickAddBar';
import { TodoTaskList } from '@/components/todo/TodoTaskList';
import { TodoCompletedSection } from '@/components/todo/TodoCompletedSection';
import { TodoScrollProvider } from '@/components/todo/TodoScrollContext';
import { TodoTaskActionSheet } from '@/components/todo/TodoTaskActionSheet';
import { TodoSortFilterMenu } from '@/components/todo/TodoSortFilterMenu';
import { TaskListTabBar } from '@/components/todo/lists/TaskListTabBar';
import { TodoListModals } from '@/components/todo/lists/TodoListModals';
import { createMoveTaskCoordinator } from '@/components/todo/lists/MoveTaskCoordinator';
import { TAB_BAR_HEIGHT } from '@/components/navigation/CustomTabBar';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import { useTodoPreferences } from '@/hooks/use-todo-preferences';
import { useTodoState } from '@/hooks/use-todo-state';
import { useTaskLists } from '@/hooks/use-task-lists';
import { useActiveTaskList } from '@/hooks/use-active-task-list';
import { useListSwipeGesture } from '@/hooks/use-list-swipe-gesture';
import { useResolvedDefaultCalendarId } from '@/hooks/use-resolved-default-calendar-id';
import { deviceCalendarService, type DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { canAccessDeviceCalendar } from '@/lib/calendar/platform';
import type { Task } from '@/types/tasks';
import type { TaskList } from '@/types/task-lists';
import type { ActionMenuAnchor } from '@/components/todo/TodoTaskItem';
import { taskEventSyncManager } from '@/lib/calendar/TaskEventSyncManager';
import { EventDescriptionParser } from '@/lib/utils/EventDescriptionParser';
import type { CreateCalendarEventInput, UpdateCalendarEventInput } from '@/types/events';
import { useChatStore } from '@/lib/chat/useChatStore';
import { useSupabaseAuth } from '@/lib/supabase';
import { TasksApiClient } from '@/lib/client/TasksApiClient';
import { TaskListsApiClient } from '@/lib/client/TaskListsApiClient';
import { ListDeletionCoordinator } from '@/lib/client/ListDeletionCoordinator';
import { Toast } from '@/components/ui/molecules/toast';

const GRADIENT_FADE_HEIGHT = 35;
/** Extra offset on Android to clear the keyboard suggestions/toolbar bar */
const KEYBOARD_TOOLBAR_HEIGHT = Platform.OS === 'android' ? 48 : 0;
/**
 * Approximate height of the floating QuickAddBar (container padding + input +
 * a small margin). Used to keep newly-focused inputs from being hidden behind
 * it when the keyboard is up.
 */
const QUICK_ADD_BAR_CLEARANCE = 80;

export default function TodoScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();
  const { keyboardHeight, isKeyboardVisible } = useKeyboardHeight();
  const quickAddBottom = isKeyboardVisible
    ? keyboardHeight + KEYBOARD_TOOLBAR_HEIGHT
    : TAB_BAR_HEIGHT + insets.bottom + GRADIENT_FADE_HEIGHT;

  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
  const quickAddRef = useRef<TodoQuickAddBarHandle>(null);

  const { session } = useSupabaseAuth();
  const currentUserId = session?.user?.id ?? null;
  const tasksClient = useMemo(() => {
    if (!session?.access_token) return null;
    return new TasksApiClient(session.access_token);
  }, [session?.access_token]);

  const {
    lists,
    defaultListId,
    atCap,
    createList,
    updateList,
    deleteList,
    reorderLists,
    refresh: refreshLists,
    setLists,
  } = useTaskLists();
  const { activeListId, setActiveListId } = useActiveTaskList(lists, defaultListId);

  const [createListVisible, setCreateListVisible] = useState(false);
  const [manageVisible, setManageVisible] = useState(false);
  const [shareTarget, setShareTarget] = useState<TaskList | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskList | null>(null);
  const [moveTarget, setMoveTarget] = useState<Task | null>(null);
  const [pendingDeleteIntoListId, setPendingDeleteIntoListId] = useState<string | null>(null);
  const highlightRef = useRef<{ current: string | null }>({ current: null });
  const [highlightTaskId, setHighlightTaskId] = useState<string | null>(null);

  const taskListsClient = useMemo(() => {
    if (!session?.access_token) return null;
    return new TaskListsApiClient(session.access_token);
  }, [session?.access_token]);

  const deletionCoordinator = useMemo(() => {
    if (!currentUserId || !taskListsClient) return null;
    return new ListDeletionCoordinator({
      userId: currentUserId,
      client: taskListsClient,
      setLists,
      onPendingTargetChange: setPendingDeleteIntoListId,
      onError: (message) => Toast.show(message, { type: 'error' }),
    });
  }, [currentUserId, taskListsClient, setLists]);

  const { preferences, setSort, setFilter, isDragEnabled } = useTodoPreferences();

  const {
    activeTasks,
    completedTasks,
    loading,
    refreshing,
    addTask,
    toggleTask,
    togglePriority,
    deleteTask,
    reorderTaskById,
    updateTaskTitle,
    updateTaskColor,
    toggleSubtask,
    addSubtask,
    updateSubtaskTitle,
    deleteSubtask,
    refresh,
  } = useTodoState({
    sort: preferences.sort,
    filter: preferences.filter,
    listId: activeListId,
    suppressInitialFetch:
      pendingDeleteIntoListId !== null && activeListId === pendingDeleteIntoListId,
  });

  const coordinator = useMemo(
    () =>
      createMoveTaskCoordinator(
        {
          setActiveListId,
          patchTaskList: async (taskId, targetListId) => {
            if (!tasksClient) return;
            await tasksClient.updateTask(taskId, { listId: targetListId });
            await refresh();
            await refreshLists();
          },
          getTaskCurrentListId: (taskId) => {
            const task = [...activeTasks, ...completedTasks].find((t) => t.id === taskId);
            return task?.listId ?? null;
          },
          revertTaskList: async (taskId, previousListId) => {
            if (!tasksClient) return;
            await tasksClient.updateTask(taskId, { listId: previousListId });
          },
          showErrorToast: (message) => Toast.show(message, { type: 'error' }),
        },
        highlightRef.current,
      ),
    [setActiveListId, tasksClient, activeTasks, completedTasks, refresh, refreshLists],
  );

  // Sibling-list navigation for swipe gestures. `lists` is already sorted by
  // orderIndex (matches the tab bar order). No wrap-around: at the ends, the
  // gesture is a no-op so users don't accidentally jump from list 5 → list 1.
  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => a.orderIndex - b.orderIndex),
    [lists],
  );
  const { prevListId, nextListId } = useMemo(() => {
    if (!activeListId || sortedLists.length <= 1) {
      return { prevListId: null, nextListId: null };
    }
    const idx = sortedLists.findIndex((l) => l.id === activeListId);
    if (idx < 0) return { prevListId: null, nextListId: null };
    return {
      prevListId: idx > 0 ? sortedLists[idx - 1].id : null,
      nextListId: idx < sortedLists.length - 1 ? sortedLists[idx + 1].id : null,
    };
  }, [sortedLists, activeListId]);

  const goToNextList = useCallback(() => {
    if (nextListId) setActiveListId(nextListId);
  }, [nextListId, setActiveListId]);
  const goToPrevList = useCallback(() => {
    if (prevListId) setActiveListId(prevListId);
  }, [prevListId, setActiveListId]);

  const swipeGesture = useListSwipeGesture({
    onSwipeLeft: goToNextList,
    onSwipeRight: goToPrevList,
    enabled: sortedLists.length > 1,
  });

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetRef = useRef(0);

  // Window-space top/bottom edges of the scroll viewport, used by drag
  // auto-scroll to detect when the finger is near an edge.
  const [dragViewport, setDragViewport] = useState({
    top: 0,
    bottom: Dimensions.get('window').height,
  });

  const handleScrollLayout = useCallback((_event: LayoutChangeEvent) => {
    const node = scrollViewRef.current as unknown as {
      measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
    } | null;
    node?.measureInWindow?.((_x, y, _w, h) => {
      setDragViewport({ top: y, bottom: y + h });
    });
  }, [scrollViewRef]);

  const handleScroll = useCallback((event: any) => {
    scrollOffsetRef.current = event.nativeEvent.contentOffset.y;
  }, []);

  const editingCountRef = useRef(0);
  const [isEditingCard, setIsEditingCard] = useState(false);
  const activeInputRef = useRef<React.RefObject<View | null> | null>(null);

  const handleSetIsEditing = useCallback((editing: boolean) => {
    editingCountRef.current += editing ? 1 : -1;
    if (editingCountRef.current < 0) editingCountRef.current = 0;
    if (!editing) {
      setTimeout(() => {
        setIsEditingCard(editingCountRef.current > 0);
      }, 100);
    } else {
      setIsEditingCard(true);
    }
  }, []);

  const handleDismissFocus = useCallback(() => {
    setFocusedTaskId(null);
    Keyboard.dismiss();
  }, []);

  const performScroll = useCallback((viewRef: React.RefObject<View | null>, kbHeight: number) => {
    if (!viewRef.current || !scrollViewRef.current) return;
    viewRef.current.measureInWindow((_x, y, _width, height) => {
      const screenHeight = Dimensions.get('window').height;
      // The QuickAddBar floats above the keyboard; subtract its clearance so
      // the focused input isn't hidden behind it.
      const visibleBottom =
        screenHeight - kbHeight - KEYBOARD_TOOLBAR_HEIGHT - QUICK_ADD_BAR_CLEARANCE;
      const inputBottom = y + height + 20;
      if (inputBottom > visibleBottom) {
        const scrollBy = inputBottom - visibleBottom;
        scrollViewRef.current?.scrollTo({
          y: scrollOffsetRef.current + scrollBy,
          animated: true,
        });
      }
    });
  }, [scrollViewRef]);

  const scrollToInput = useCallback((viewRef: React.RefObject<View | null>) => {
    activeInputRef.current = viewRef;
    // Immediate attempt + delayed retry after keyboard animates
    performScroll(viewRef, keyboardHeight);
    setTimeout(() => performScroll(viewRef, keyboardHeight), 350);
  }, [keyboardHeight, performScroll]);

  // Bring an entire card (including its newly-revealed "+ Add subtask" tail)
  // above the QuickAddBar / keyboard when it expands.
  const scrollToCard = useCallback((viewRef: React.RefObject<View | null>) => {
    performScroll(viewRef, keyboardHeight);
    setTimeout(() => performScroll(viewRef, keyboardHeight), 250);
  }, [keyboardHeight, performScroll]);

  // Re-scroll when keyboard height changes (keyboard appearing/resizing)
  useEffect(() => {
    if (activeInputRef.current && isEditingCard && keyboardHeight > 0) {
      performScroll(activeInputRef.current, keyboardHeight);
    }
    if (!isEditingCard) {
      activeInputRef.current = null;
    }
  }, [keyboardHeight, isEditingCard, performScroll]);

  const scrollContextValue = useMemo(
    () => ({ scrollToInput, scrollToCard, setIsEditing: handleSetIsEditing }),
    [scrollToInput, scrollToCard, handleSetIsEditing],
  );

  const [actionSheetTask, setActionSheetTask] = useState<Task | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<ActionMenuAnchor | null>(null);
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [schedulingTask, setSchedulingTask] = useState<Task | null>(null);
  const [writableCalendars, setWritableCalendars] = useState<DeviceCalendar[]>([]);
  const storedDefaultCalendarId = useResolvedDefaultCalendarId(writableCalendars);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const isDraggingCard = !scrollEnabled;

  // Refresh task list when a task is created from AI chat, then scroll to it
  const taskRefreshSignal = useChatStore((state) => state.taskRefreshSignal);
  useEffect(() => {
    if (taskRefreshSignal > 0) {
      refresh().then(() => {
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 300);
      });
    }
  }, [taskRefreshSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTask = useCallback(async (title: string, priority?: 'high' | null) => {
    await addTask(title, priority);
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [addTask, scrollViewRef]);

  useEffect(() => {
    const loadCalendars = async () => {
      if (!canAccessDeviceCalendar()) return;
      const { status } = await deviceCalendarService.requestPermissions();
      if (status !== 'granted') return;
      const calendars = await deviceCalendarService.getDeviceCalendars();
      setWritableCalendars(calendars.filter(cal => cal.allowsModifications));
    };
    loadCalendars();
  }, []);

  const handleActionPress = useCallback((task: Task, anchor: ActionMenuAnchor) => {
    setActionSheetTask(task);
    setActionMenuAnchor(anchor);
  }, []);

  const handleScheduleAsEvent = useCallback((task: Task) => {
    setSchedulingTask(task);
    setEventModalVisible(true);
  }, []);

  const handleEventSave = useCallback(
    async (input: (CreateCalendarEventInput | UpdateCalendarEventInput) & { targetCalendarId?: string }) => {
      setEventModalVisible(false);

      // Write to device calendar so event appears in native calendar and FimFam calendar views
      const targetCalendarId = input.targetCalendarId ?? writableCalendars[0]?.id;
      if (!targetCalendarId) {
        console.warn('[TodoScreen] No writable calendar available for device sync');
        return;
      }

      await taskEventSyncManager.createDeviceEvent(
        {
          title: (input as CreateCalendarEventInput).title ?? schedulingTask?.title ?? '',
          startDate: (input as CreateCalendarEventInput).startDate,
          startTime: (input as CreateCalendarEventInput).startTime,
          endDate: (input as CreateCalendarEventInput).endDate,
          endTime: (input as CreateCalendarEventInput).endTime,
          allDay: (input as CreateCalendarEventInput).allDay,
          description: (input as CreateCalendarEventInput).description,
          location: (input as CreateCalendarEventInput).location,
          subtasks: schedulingTask?.subtasks,
        },
        targetCalendarId,
      );

      setSchedulingTask(null);
    },
    [writableCalendars, schedulingTask],
  );

  const handleEventDismiss = useCallback(() => {
    setEventModalVisible(false);
    setSchedulingTask(null);
  }, []);

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: palette.background }]} edges={['bottom']}>
      <ScreenHeaderContainer flatBottom>
        <TaskListTabBar
          lists={lists}
          activeListId={activeListId}
          currentUserId={currentUserId}
          atCap={atCap}
          onSelectList={setActiveListId}
          onRequestCreateList={() => setCreateListVisible(true)}
          rightAccessory={
            <TodoSortFilterMenu
              sort={preferences.sort}
              filter={preferences.filter}
              onSortChange={setSort}
              onFilterChange={setFilter}
              onOpenManageLists={() => setManageVisible(true)}
            />
          }
        />
      </ScreenHeaderContainer>

      <GestureDetector gesture={swipeGesture}>
        <Animated.ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          scrollEnabled={scrollEnabled}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: quickAddBottom + 60 }]}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={palette.primary} />
          }
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
          onLayout={handleScrollLayout}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        >
          <Pressable onPress={handleDismissFocus} style={styles.scrollPressable}>
            {loading ? (
              <TaskListSkeleton />
            ) : (
              <TodoScrollProvider value={scrollContextValue}>
                <TodoTaskList
                  tasks={activeTasks}
                  focusedTaskId={focusedTaskId}
                  onFocusChange={setFocusedTaskId}
                  onToggle={toggleTask}
                  onReorder={reorderTaskById}
                  onActionPress={handleActionPress}
                  onToggleSubtask={toggleSubtask}
                  onUpdateTitle={updateTaskTitle}
                  onAddSubtask={addSubtask}
                  onUpdateSubtaskTitle={updateSubtaskTitle}
                  onDeleteSubtask={deleteSubtask}
                  scrollEnabled={scrollEnabled}
                  onScrollEnabledChange={setScrollEnabled}
                  isDragEnabled={isDragEnabled}
                  highlightedTaskId={highlightTaskId}
                  scrollRef={scrollViewRef}
                  viewportTop={dragViewport.top}
                  viewportBottom={dragViewport.bottom}
                />
                <View style={isDraggingCard ? styles.dimmedDuringDrag : undefined}>
                  <TodoCompletedSection tasks={completedTasks} onToggle={toggleTask} />
                </View>
              </TodoScrollProvider>
            )}
          </Pressable>
        </Animated.ScrollView>
      </GestureDetector>

      <View style={[styles.quickAddContainer, { bottom: quickAddBottom }, isDraggingCard && styles.dimmedDuringDrag]}>
        <TodoQuickAddBar ref={quickAddRef} onAddTask={handleAddTask} />
      </View>

      <TodoTaskActionSheet
        visible={!!actionSheetTask}
        task={actionSheetTask}
        anchor={actionMenuAnchor}
        onDismiss={() => { setActionSheetTask(null); setActionMenuAnchor(null); }}
        onScheduleAsEvent={handleScheduleAsEvent}
        onTogglePriority={togglePriority}
        onDelete={deleteTask}
        onColorChange={updateTaskColor}
        onRequestMoveToList={(task) => setMoveTarget(task)}
      />

      <TodoListModals
        lists={lists}
        activeListId={activeListId}
        defaultListId={defaultListId}
        currentUserId={currentUserId}
        atCap={atCap}
        activeTasks={activeTasks}
        completedTasks={completedTasks}
        coordinator={coordinator}
        deletionCoordinator={deletionCoordinator}
        createListVisible={createListVisible}
        setCreateListVisible={setCreateListVisible}
        moveTarget={moveTarget}
        setMoveTarget={setMoveTarget}
        manageVisible={manageVisible}
        setManageVisible={setManageVisible}
        shareTarget={shareTarget}
        setShareTarget={setShareTarget}
        deleteTarget={deleteTarget}
        setDeleteTarget={setDeleteTarget}
        setActiveListId={setActiveListId}
        setHighlightTaskId={setHighlightTaskId}
        createList={createList}
        updateList={updateList}
        deleteList={deleteList}
        reorderLists={reorderLists}
      />

      <CalendarEventModal
        visible={eventModalVisible}
        mode="create"
        defaultTitle={schedulingTask?.title ?? ''}
        defaultDescription={
          schedulingTask?.subtasks?.length
            ? EventDescriptionParser.serialize(
                [],
                schedulingTask.subtasks.map((s, i) => ({
                  id: s.id,
                  text: s.title,
                  completed: s.completed,
                  lineIndex: i,
                }))
              )
            : undefined
        }
        writableCalendars={writableCalendars}
        defaultCalendarId={storedDefaultCalendarId ?? writableCalendars[0]?.id ?? null}
        onSave={handleEventSave}
        onDismiss={handleEventDismiss}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  scrollPressable: {
    flexGrow: 1,
  },
  quickAddContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
  },
  dimmedDuringDrag: {
    opacity: 0.5,
  },
});
